import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useParams } from 'react-router-dom';
import { ToastStack } from '@repo/common';
import { ReactComponent as BackIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/16px/icon_16px_check-circle_outline.svg';
import {
  generateOpsReportAi,
  getOpsReport,
  sendOpsReportEmail,
  setOpsReportStatus,
} from '../../../api/twoDays/ops.api';
import TdButton from '../../../components/TwoDays/Button';
import { Modal, TextArea, TextInput } from '../../../components/TwoDays/Modal';
import { jobLabel } from '../../../components/TwoDays/ops/opsModel';
import { cn } from '../../../components/TwoDays/model/derive';
import { StatusChip } from '../../../components/TwoDays/primitives';
import { LockedLines, ReportBlock } from '../../../components/TwoDays/report/ReportBlocks';
import { pdfBlob, reportFileName, savePdf } from '../../../components/TwoDays/report/reportPdf';
import {
  SAMPLE_REPORT,
  STATUS,
  emailMessage,
  freeShape,
  withContent,
} from '../../../components/TwoDays/report/reportModel';

/**
 * Growth Report 뷰어 (Ops · 새 탭) — 📋 Growth Report 초안 (2차 핸드오프 C6~C8 · C15 · C16).
 *   상단 바: 상태 · AI 초안 안내 · Free/Full · Confirm → Download PDF / Email 활성 · Close
 *   Full = 섹션 1~16 전부 (16 은 자가진단 연동 동의 시만) · Free = 머리말과 섹션 제목만, 값은 넘기지 않고 블러 + Unlock 바
 * 섹션 1~4 · 6 은 동료 평가·회고·Kit 과 AI 초안, 5 · 7~16 은 워크보드 기록 집계(서버 content)로 그린다. sample 주소만 초안 값.
 * AI 초안은 Draft ready 리포트를 처음 열 때 만든다 (실패하면 Retry). Waiting · Not eligible 도 열리지만 Confirm 은 못 한다.
 */

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const OPENABLE = ['draft', 'confirmed', 'sent', 'failed'];

/** "Oct 15–17, 2026" (KST) */
const dateRange = (startIso, endIso) => {
  const f = (iso, opts) =>
    new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', ...opts }).format(new Date(iso));
  const sm = f(startIso, { month: 'short' });
  const em = f(endIso, { month: 'short' });
  const sd = f(startIso, { day: 'numeric' });
  const ed = f(endIso, { day: 'numeric' });
  const y = f(endIso, { year: 'numeric' });
  return sm === em ? `${sm} ${sd}–${ed}, ${y}` : `${sm} ${sd} – ${em} ${ed}, ${y}`;
};

// 완주 전·이탈이면 머리말 칩도 그대로 보여 준다 (Reports 목록과 같은 문구)
const COMPLETION_CHIP = {
  in_progress: { label: 'Not completed', tone: 'gray' },
  peer_review_open: { label: 'Peer review open', tone: 'success' },
  dropped: { label: 'Dropped', tone: 'gray' },
  not_completed: { label: 'Not completed', tone: 'gray' },
};

const NOT_READY = {
  waiting:
    'Not ready yet. It becomes Draft ready once all teammate reviews are in or peer review closes.',
  not_eligible:
    'Not eligible. There’s no report for people who dropped out or didn’t submit the peer review.',
};

/** 서버 머리말(이름·프로젝트·역할·팀·기간·상태)과 본문(content)을 리포트 모양에 붙인다 */
const withHead = (head, aiState) => {
  const { row } = head;
  return {
    ...SAMPLE_REPORT,
    person: {
      ...SAMPLE_REPORT.person,
      id: row.memberId,
      program: head.cohort ?? null, // 회차 — 신청서의 모집 단위 (모집마다 바뀐다)
      name: row.name,
      email: row.email,
      project: head.projectName,
      completion: row.completion === 'completed_late' ? 'late' : row.completion,
      facts: [
        ['Role', `${jobLabel(row.job)}${row.leader ? ' · Leader' : ''}`],
        ['Team', row.teamName],
        ['Team size', `${head.teamSize} people`],
        ['Dates', dateRange(head.startAt, head.endAt)],
      ],
    },
    sections: withContent(SAMPLE_REPORT.sections, head.content, aiState),
    status: row.report,
    selfLinked: head.selfLinked,
  };
};

const Section = ({ s, pdf }) => (
  <section
    aria-label={s.title}
    className={cn(
      'flex flex-col gap-3.5 rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 px-4 py-4 md:px-6 md:py-5',
      !pdf && 'shadow-4',
      pdf && 'break-inside-avoid',
    )}
  >
    <div className='flex items-baseline gap-2.5'>
      <span className='text-[13px] font-medium tabular-nums text-text-secondary'>{s.n}</span>
      <h2 className='text-[17px] font-medium leading-[140%]'>{s.title}</h2>
    </div>
    {s.locked ? (
      <LockedLines lines={s.lines} />
    ) : (
      <>
        {s.desc && (
          <p className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
            {s.desc}
          </p>
        )}
        {s.blocks.map((b, i) => (
          <ReportBlock key={i} block={b} />
        ))}
      </>
    )}
  </section>
);

const ReportBody = ({ report, pdf }) => {
  const { person } = report;
  const sections = report.sections.filter((s) => !s.onlyIfSelfLinked || report.selfLinked);
  return (
    <div className='flex flex-col gap-4'>
      <section
        className={cn(
          'flex flex-col gap-5 rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 px-5 py-6 md:px-8',
          !pdf && 'shadow-4',
        )}
      >
        {/* 왼쪽: 회차 · 이름 · 프로젝트 / 오른쪽: 완주 칩 · 수료증 */}
        <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3'>
          <div className='flex min-w-0 flex-col gap-1.5'>
            <span className='text-[14px] text-text-secondary'>
              Growth report · 2 days project{person.program ? ` · ${person.program}` : ''}
            </span>
            <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] md:text-[34px]'>
              {person.name}
            </h1>
            <span className='text-[16px] text-text-secondary'>{person.project}</span>
          </div>
          <div className='flex shrink-0 items-center gap-4'>
            {COMPLETION_CHIP[person.completion] ? (
              <StatusChip chip={COMPLETION_CHIP[person.completion]} />
            ) : (
              <span className='inline-flex h-7 items-center gap-1 rounded-lg border border-functional-success02 bg-functional-success01 px-2 text-[12px] font-medium text-text-primary'>
                <CheckCircleIcon className='h-3.5 w-3.5 text-functional-success03' />
                Completed
              </span>
            )}
            {!pdf && (
              <span className='text-[15px] font-medium text-green-deep02 underline underline-offset-4'>
                View certificate
              </span>
            )}
          </div>
        </div>
        <div className='grid grid-cols-2 gap-x-3 gap-y-4 pt-2 md:grid-cols-4'>
          {person.facts.map(([l, v]) => (
            <div key={l} className='flex flex-col gap-1'>
              <span className='text-[13px] text-text-secondary'>{l}</span>
              <span className='text-[16px] font-medium'>{v}</span>
            </div>
          ))}
        </div>
        {person.completion === 'late' && (
          <span className='w-fit'>
            <StatusChip chip={{ label: 'Peer review submitted late', tone: 'warning' }} />
          </span>
        )}
      </section>
      {sections.map((s) => (
        <Section key={s.n} s={s} pdf={pdf} />
      ))}
      <p className='px-1 pt-2 text-[12px] leading-[160%] text-text-secondary'>
        This report is based on what was recorded in the program. It is a reference, not a
        certification.
        <br />
        Questions? Contact{' '}
        <a
          href='mailto:contact@ko-ready.com'
          className='text-green-deep02 underline underline-offset-2 transition-colors hover:text-green-deep04'
        >
          contact@ko-ready.com
        </a>{' '}
        within 14 days.
      </p>
    </div>
  );
};

/** 리포트 메일 — 받는 사람 · 본문 · 보낼 것(Free card / Full report). Send 하면 PDF 를 만들어 바로 보낸다 */
const EmailDialog = ({ report, onClose, onSend }) => {
  const [kind, setKind] = useState('full');
  const [to, setTo] = useState(report.person.email ?? '');
  const [msg, setMsg] = useState(() => emailMessage('full', report));
  const [edited, setEdited] = useState(false);
  const [error, setError] = useState('');
  const [sendError, setSendError] = useState('');
  const [sending, setSending] = useState(false);
  const pick = (k) => {
    if (k === kind) return;
    setKind(k);
    // 고친 문구가 없으면 종류에 맞는 기본 문구로 바꾼다
    if (!edited) setMsg(emailMessage(k, report));
  };
  const send = async () => {
    if (!EMAIL_RE.test(to.trim())) {
      setError('Add an email address, e.g. participant@example.com');
      return;
    }
    if (!msg.trim()) {
      setSendError('Write a message.');
      return;
    }
    setSending(true);
    setSendError('');
    const err = await onSend({ kind, to: to.trim(), msg });
    setSending(false);
    if (err) setSendError(err);
  };
  return (
    <Modal
      open
      sheetOnMobile
      title='Email report'
      subtitle={[report.person.name, report.person.program || report.person.project]
        .filter(Boolean)
        .join(' · ')}
      width='max-w-[560px]'
      onClose={sending ? () => {} : onClose}
      footer={
        <>
          <TdButton variant='soft' size='l' disabled={sending} onClick={onClose} className='px-5'>
            Cancel
          </TdButton>
          <TdButton size='l' disabled={sending} onClick={send}>
            {sending ? 'Sending…' : 'Send'}
          </TdButton>
        </>
      }
    >
      <label className='flex flex-col gap-1.5'>
        <span className='text-[14px] font-medium'>To</span>
        <TextInput
          type='email'
          placeholder='participant@example.com'
          value={to}
          disabled={sending}
          aria-invalid={!!error}
          onChange={(e) => {
            setTo(e.target.value);
            setError('');
          }}
          className={cn('h-12 text-[15px]', error && 'border-functional-failed03')}
        />
        {error && <span className='text-[12px] text-functional-failed04'>{error}</span>}
      </label>
      <label className='flex flex-col gap-1.5'>
        <span className='text-[14px] font-medium'>Message</span>
        <TextArea
          value={msg}
          disabled={sending}
          onChange={(e) => {
            setMsg(e.target.value);
            setEdited(true);
          }}
          className='min-h-[240px] text-[15px] leading-[160%]'
        />
      </label>
      <div className='flex flex-col gap-1.5'>
        <span className='text-[14px] font-medium'>Send</span>
        <div role='radiogroup' aria-label='What to send' className='grid gap-2 sm:grid-cols-2'>
          {[
            ['free', 'Free card', 'Header and section titles'],
            ['full', 'Full report', 'Everything in this report'],
          ].map(([k, label, sub]) => (
            <button
              key={k}
              type='button'
              role='radio'
              aria-checked={kind === k}
              disabled={sending}
              onClick={() => pick(k)}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-xl px-4 py-3 text-left',
                kind === k
                  ? 'bg-surface-green10 text-green-deep04 shadow-[inset_0_0_0_1.5px_#1dc37b]'
                  : 'bg-grayscale-gray00 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)]',
              )}
            >
              <span className='text-[15px] font-medium'>{label}</span>
              <span className='text-[13px] text-text-secondary'>{sub}</span>
            </button>
          ))}
        </div>
      </div>
      <p className='text-[13px] text-text-secondary'>
        The {kind === 'free' ? 'Free card' : 'full report'} is attached as a PDF.
      </p>
      {sendError && (
        <p role='alert' className='text-[13px] text-functional-failed04'>
          {sendError}
        </p>
      )}
    </Modal>
  );
};

/** A4 한 장 너비(794px)로 리포트를 그린다 — 미리보기 · 다운로드 · 메일 첨부가 같은 모양 */
const PdfPage = React.forwardRef(({ report }, ref) => (
  <div ref={ref} className='w-[794px] bg-grayscale-gray00 px-14 py-12 text-text-primary'>
    <ReportBody report={report} pdf />
  </div>
));
PdfPage.displayName = 'PdfPage';

/** 화면 밖에서 그린 뒤 PDF Blob 으로 (메일 첨부용) */
const renderPdfBlob = async (report, filename) => {
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;';
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    let node = null;
    flushSync(() =>
      root.render(
        <PdfPage
          report={report}
          ref={(n) => {
            node = n;
          }}
        />,
      ),
    );
    await document.fonts?.ready;
    return await pdfBlob(node, filename);
  } finally {
    root.unmount();
    host.remove();
  }
};

const OpsGrowthReport = () => {
  const { reportId } = useParams();
  // 'sample' 이면 디자인 초안 그대로 (데모용)
  const sample = reportId === 'sample' || !reportId;
  const [head, setHead] = useState(null);
  const [aiState, setAiState] = useState(null); // null · 'loading' · 'error'
  const [loadError, setLoadError] = useState('');
  const [sampleStatus, setSampleStatus] = useState(SAMPLE_REPORT.status);
  const aiTried = useRef(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('full');
  const [pdf, setPdf] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    if (sample) return;
    getOpsReport(reportId)
      .then(setHead)
      .catch(() => setLoadError('Couldn’t load this report.'));
  }, [reportId, sample]);

  /** AI 초안 만들기 — 서버가 Groq 로 섹션 2 요약 · 섹션 6 을 쓴다 */
  const writeAi = async () => {
    setAiState('loading');
    try {
      const next = await generateOpsReportAi(reportId);
      setHead(next);
      setAiState(next.content?.ai?.status === 'error' ? 'error' : null);
    } catch {
      setAiState('error');
    }
  };
  // Draft ready 인데 AI 초안이 아직 없으면 처음 열 때 한 번 만든다
  useEffect(() => {
    if (!head || aiTried.current) return;
    if (head.row.report === 'draft' && head.content?.ai?.status === 'none') {
      aiTried.current = true;
      writeAi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head]);

  const report = sample
    ? { ...SAMPLE_REPORT, status: sampleStatus }
    : head && {
        ...withHead(head, aiState),
        // AI 자리의 Retry — Draft ready 일 때만
        sections: withHead(head, aiState).sections.map((sec) => ({
          ...sec,
          blocks: sec.blocks.map((b) =>
            b.type === 'aiPending' && head.row.report === 'draft' ? { ...b, onRetry: writeAi } : b,
          ),
        })),
      };

  if (!report) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-bg-twodays p-6 text-[14px] text-text-secondary'>
        {loadError || 'Loading…'}
      </div>
    );
  }

  const ready = OPENABLE.includes(report.status);
  const confirmed = ready && report.status !== 'draft';
  const status = STATUS[report.status];
  const shown = view === 'free' && !pdf ? freeShape(report) : report;
  const free = view === 'free' && !pdf;

  /** 상태 저장 — 서버에 남기고(상태 기록 카드) 받은 머리말로 바꾼다. 실패하면 false */
  const setStatus = async (s, note) => {
    if (sample) {
      setSampleStatus(s);
      return true;
    }
    setBusy(true);
    try {
      setHead(await setOpsReportStatus(reportId, { status: s, note: note ?? null }));
      return true;
    } catch (e) {
      setToast({ title: 'Couldn’t save', message: e.response?.data?.msg || 'Try again.' });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (pdf) {
      setPdf(false);
      return;
    }
    window.close();
  };

  return (
    <div className='min-h-screen bg-bg-twodays text-text-primary print:bg-grayscale-gray00'>
      {/* 상단 바 — 인쇄할 때는 숨긴다 */}
      <div className='sticky top-0 z-20 flex min-h-16 flex-wrap items-center gap-x-3 gap-y-2 border-b border-green-deep03/[0.08] bg-grayscale-gray00 px-4 py-2.5 md:px-8 print:hidden'>
        <button
          type='button'
          onClick={close}
          className='flex h-11 items-center gap-1.5 px-1 text-[14px] font-medium'
        >
          <BackIcon className='h-4 w-4' />
          {pdf ? 'Close preview' : 'Close'}
        </button>
        {pdf ? (
          <>
            <span className='min-w-[160px] flex-1 text-[15px] font-medium'>
              PDF preview · A4 · same order as the report
            </span>
            <TdButton
              size='l'
              disabled={downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await savePdf(pageRef.current, reportFileName(report.person.name));
                } catch {
                  setToast({ title: 'Couldn’t make the PDF', message: 'Try again.' });
                } finally {
                  setDownloading(false);
                }
              }}
              // 밝은 초록은 PDF 미리보기 전용 색 — 꺼졌을 때도 같은 색에 흐리게
              className='border-functional-success03 bg-functional-success03 text-grayscale-gray00 hover:border-green-deep04 hover:bg-green-deep04 disabled:opacity-60'
            >
              {downloading ? 'Preparing…' : 'Download PDF'}
            </TdButton>
          </>
        ) : (
          <>
            <StatusChip chip={{ label: status.label, tone: status.tone }} />
            <span className='min-w-[160px] flex-1 text-[13px] text-text-secondary'>
              AI-assisted draft · confirm before sending
            </span>
            <div
              role='tablist'
              aria-label='Report view'
              className='flex rounded-full bg-green-deep03/[0.06] p-[3px]'
            >
              {[
                ['free', 'Free'],
                ['full', 'Full'],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type='button'
                  role='tab'
                  aria-selected={view === k}
                  onClick={() => setView(k)}
                  className={cn(
                    'h-[34px] rounded-full px-4 text-[13px] font-medium',
                    view === k ? 'bg-grayscale-gray00 shadow-4' : 'text-text-secondary',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className='flex flex-wrap gap-2'>
              {ready &&
                (confirmed ? (
                  // 확인 완료 — 다시 누를 수 없다
                  <TdButton
                    disabled
                    aria-label='Confirmed'
                    className='cursor-default bg-grayscale-gray02'
                  >
                    Confirmed
                  </TdButton>
                ) : (
                  <TdButton
                    disabled={busy || aiState === 'loading'}
                    title={aiState === 'loading' ? 'Wait for the AI draft' : undefined}
                    onClick={async () => {
                      if (!(await setStatus('confirmed'))) return;
                      setToast({
                        title: 'Report confirmed',
                        message: 'You can download or email it now',
                      });
                    }}
                  >
                    Confirm
                  </TdButton>
                ))}
              <TdButton
                variant='soft'
                disabled={!confirmed}
                title={confirmed ? undefined : 'Available after Confirm'}
                onClick={() => {
                  setView('full');
                  setPdf(true);
                  window.scrollTo(0, 0);
                }}
                className='bg-green-deep03/[0.08] px-[18px] hover:bg-green-deep03/[0.14]'
              >
                Download PDF
              </TdButton>
              <TdButton
                variant='soft'
                disabled={!confirmed}
                title={confirmed ? undefined : 'Available after Confirm'}
                onClick={() => setEmailOpen(true)}
                className='bg-green-deep03/[0.08] px-[18px] hover:bg-green-deep03/[0.14]'
              >
                Email
              </TdButton>
            </div>
          </>
        )}
      </div>

      {/* Waiting · Not eligible — 열어 볼 수는 있지만 아직(또는 아예) 보낼 리포트가 아니다 */}
      {!ready && (
        <div className='border-b border-green-deep03/[0.08] bg-grayscale-gray02 px-4 py-3 text-[14px] text-text-primary md:px-8 print:hidden'>
          {NOT_READY[report.status]}
        </div>
      )}

      {/* Free — 값 대신 잠금, 위에 Unlock 바 */}
      {free && (
        <div className='sticky top-[64px] z-10 flex flex-wrap items-center justify-between gap-3 border-b border-green-deep03/[0.08] bg-surface-green10 px-4 py-3 md:px-8 max-md:top-[120px]'>
          <span className='text-[14px] text-text-primary'>
            Scores, teammate comments and next actions are in the full report.
          </span>
          <TdButton
            onClick={() =>
              setToast({
                title: 'Unlock full report',
                message: 'Opens checkout · same as Career Report',
              })
            }
          >
            Unlock full report
          </TdButton>
        </div>
      )}

      <main
        className={cn(
          'flex justify-center px-4 py-6 md:px-12 md:py-10',
          pdf && 'overflow-x-auto bg-grayscale-gray02',
        )}
      >
        {pdf ? (
          <div className='shadow-3'>
            <PdfPage ref={pageRef} report={shown} />
          </div>
        ) : (
          <div className='w-full max-w-[880px]'>
            <ReportBody report={shown} />
          </div>
        )}
      </main>

      {emailOpen && (
        <EmailDialog
          report={report}
          onClose={() => setEmailOpen(false)}
          onSend={async ({ kind, to, msg }) => {
            const label = kind === 'free' ? 'Free card' : 'Full report';
            if (sample) {
              setEmailOpen(false);
              setSampleStatus('sent');
              setToast({ title: 'Report sent', message: `${label} · ${to}` });
              return null;
            }
            const filename = reportFileName(report.person.name, kind);
            let blob;
            try {
              blob = await renderPdfBlob(kind === 'free' ? freeShape(report) : report, filename);
            } catch {
              return 'Couldn’t make the PDF. Try again.';
            }
            try {
              const next = await sendOpsReportEmail(reportId, {
                to,
                kind,
                message: msg,
                pdf: blob,
                filename,
              });
              setHead(next);
              setEmailOpen(false);
              if (next.row.report === 'failed') {
                setToast({ title: 'Send failed', message: 'Check the address and try again.' });
              } else {
                setToast({ title: 'Report sent', message: `${label} · ${to}` });
              }
              return null;
            } catch (e) {
              return e.response?.data?.msg || 'Couldn’t send. Try again.';
            }
          }}
        />
      )}

      {/* 디자인 시스템 Toast — 오른쪽 아래. 실패 문구면 error */}
      <div className='print:hidden'>
        <ToastStack
          toasts={
            toast
              ? [
                  {
                    id: `report:${toast.title}`,
                    type: /couldn’t|failed/i.test(toast.title) ? 'error' : 'success',
                    state: 'white',
                    title: toast.title,
                    message: toast.message,
                  },
                ]
              : []
          }
          onClose={() => setToast(null)}
        />
      </div>
    </div>
  );
};

export default OpsGrowthReport;
